import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Link, 
  Search,
  ExternalLink,
  Send,
  Loader2,
  CheckCircle2,
  Clock,
  Sparkles,
  AlertCircle,
  Mail,
  FileText,
  Eye,
  Edit3,
  Trash2,
  RefreshCw,
  Inbox,
  CheckCheck,
  XCircle,
  Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to validate email
const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  
  const invalidPatterns = [
    /^tbd$/i,
    /^n\/a$/i,
    /^check\s/i,
    /^caută\s/i,
    /^find\s/i,
    /^contact@example/i,
    /^info@example/i,
    /^email@example/i,
    /^test@/i,
    /example\.com$/i,
    /domain\.com$/i,
    /website\.com$/i,
    /yoursite\.com$/i,
    /placeholder/i,
    /\.test$/i,
    /^-$/,
    /^\.$/,
    /^\s*$/
  ];
  
  // Must contain @ and a dot after @
  if (!email.includes('@') || !email.split('@')[1]?.includes('.')) return false;
  
  // Check against invalid patterns
  for (const pattern of invalidPatterns) {
    if (pattern.test(email)) return false;
  }
  
  return true;
};

export default function BacklinksPage() {
  const [backlinks, setBacklinks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [outreachList, setOutreachList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [searchingNew, setSearchingNew] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [requesting, setRequesting] = useState(null);
  const [preparingOutreach, setPreparingOutreach] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [outreachFilter, setOutreachFilter] = useState('draft'); // 'all', 'draft', 'sent', 'responded'
  const [activeTab, setActiveTab] = useState('opportunities');
  
  // Outreach dialog state
  const [showOutreachDialog, setShowOutreachDialog] = useState(false);
  const [currentOutreach, setCurrentOutreach] = useState(null);
  const [editingEmail, setEditingEmail] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  
  const { getAuthHeaders } = useAuth();
  const { currentSite, sites } = useSite();

  useEffect(() => {
    if (currentSite?.niche) {
      fetchBacklinks();
    } else {
      setLoading(false);
    }
    fetchRequests();
    fetchOutreach();
  }, [currentSite?.id]); // Use id instead of whole object

  const fetchBacklinks = async () => {
    if (!currentSite?.niche) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/backlinks/niche/${encodeURIComponent(currentSite.niche)}`, { 
        headers: getAuthHeaders() 
      });
      setBacklinks(response.data);
    } catch (error) {
      console.error('Failed to fetch backlinks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/backlinks/requests`, { headers: getAuthHeaders() });
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    }
  };

  const fetchOutreach = async () => {
    try {
      const url = currentSite?.id 
        ? `${API}/backlinks/outreach?site_id=${currentSite.id}`
        : `${API}/backlinks/outreach`;
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setOutreachList(response.data);
    } catch (error) {
      console.error('Failed to fetch outreach:', error);
    }
  };

  const handleSearchNewOpportunities = async () => {
    if (!currentSite?.id) {
      toast.error('Selectează un site mai întâi');
      return;
    }
    
    setSearchingNew(true);
    try {
      const response = await axios.post(
        `${API}/backlinks/search-opportunities?site_id=${currentSite.id}`, 
        {}, 
        { headers: getAuthHeaders() }
      );
      toast.success(`${response.data.new_opportunities} oportunități noi găsite pentru ${currentSite.site_name || currentSite.site_url}!`);
      fetchBacklinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la căutarea oportunităților');
    } finally {
      setSearchingNew(false);
    }
  };

  const handleGenerateBacklinks = async () => {
    if (!currentSite?.niche) {
      toast.error('Site-ul selectat nu are o nișă definită. Editează site-ul și adaugă nișa.');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.get(`${API}/backlinks/generate/${encodeURIComponent(currentSite.niche)}`, { 
        headers: getAuthHeaders() 
      });
      setBacklinks(response.data);
      toast.success(`${response.data.length} oportunități de backlink generate pentru "${currentSite.niche}"!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la generarea backlinks');
    } finally {
      setGenerating(false);
    }
  };

  const handlePrepareOutreach = async (backlink) => {
    if (!currentSite?.id) {
      toast.error('Selectează un site mai întâi');
      return;
    }

    setPreparingOutreach(backlink.id);
    try {
      const response = await axios.post(
        `${API}/backlinks/outreach/prepare`,
        null,
        { 
          headers: getAuthHeaders(),
          params: {
            backlink_id: backlink.id,
            site_id: currentSite.id
          }
        }
      );
      
      setCurrentOutreach(response.data);
      setEditSubject(response.data.email.subject);
      setEditBody(response.data.email.body);
      setShowOutreachDialog(true);
      setEditingEmail(false);
      
      // Add to requests if not already there
      if (!isRequested(backlink.id)) {
        setRequests([...requests, {
          id: backlink.id,
          domain: backlink.domain,
          status: 'outreach_prepared',
          type: backlink.type,
          price: backlink.price
        }]);
      }
      
      toast.success('Email de outreach pregătit!');
      fetchOutreach();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la pregătirea outreach');
    } finally {
      setPreparingOutreach(null);
    }
  };

  const handleSendEmail = async (outreachId) => {
    setSendingEmail(outreachId || currentOutreach?.id);
    try {
      // If editing, save changes first
      if (editingEmail && currentOutreach) {
        await axios.put(
          `${API}/backlinks/outreach/${currentOutreach.id}`,
          null,
          {
            headers: getAuthHeaders(),
            params: {
              subject: editSubject,
              body: editBody
            }
          }
        );
      }
      
      const id = outreachId || currentOutreach?.id;
      const response = await axios.post(
        `${API}/backlinks/outreach/${id}/send`,
        {},
        { headers: getAuthHeaders() }
      );
      
      toast.success(response.data.message);
      setShowOutreachDialog(false);
      setCurrentOutreach(null);
      fetchOutreach();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la trimiterea emailului');
    } finally {
      setSendingEmail(null);
    }
  };

  const handleDeleteOutreach = async (outreachId) => {
    try {
      await axios.delete(`${API}/backlinks/outreach/${outreachId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Outreach șters');
      fetchOutreach();
    } catch (error) {
      toast.error('Eroare la ștergere');
    }
  };

  const handleMarkResponded = async (outreachId, responseType) => {
    try {
      await axios.post(
        `${API}/backlinks/outreach/${outreachId}/mark-responded`,
        null,
        {
          headers: getAuthHeaders(),
          params: { response_type: responseType }
        }
      );
      toast.success('Status actualizat');
      fetchOutreach();
    } catch (error) {
      toast.error('Eroare la actualizare');
    }
  };

  const handleBulkDelete = async (status) => {
    const count = outreachList.filter(o => o.status === status).length;
    if (count === 0) {
      toast.error('Nu există emailuri de șters');
      return;
    }
    
    if (!window.confirm(`Sigur vrei să ștergi toate ${count} emailurile cu status "${status}"?`)) {
      return;
    }
    
    try {
      const response = await axios.delete(
        `${API}/backlinks/outreach/bulk/${status}`,
        {
          headers: getAuthHeaders(),
          params: { site_id: currentSite?.id }
        }
      );
      toast.success(`${response.data.deleted_count} emailuri șterse`);
      fetchOutreach();
    } catch (error) {
      toast.error('Eroare la ștergere în bloc');
    }
  };

  const handleDeleteInvalidEmails = async () => {
    const invalidOutreach = outreachList.filter(o => 
      o.status === 'draft' && !isValidEmail(o.contact_email || o.target_email)
    );
    
    if (invalidOutreach.length === 0) {
      toast.info('Nu există outreach-uri cu emailuri invalide');
      return;
    }
    
    if (!window.confirm(`Sigur vrei să ștergi ${invalidOutreach.length} outreach-uri cu emailuri invalide?`)) {
      return;
    }
    
    let deletedCount = 0;
    for (const outreach of invalidOutreach) {
      try {
        await axios.delete(`${API}/backlinks/outreach/${outreach.id}`, {
          headers: getAuthHeaders()
        });
        deletedCount++;
      } catch (error) {
        console.error(`Failed to delete outreach ${outreach.id}:`, error);
      }
    }
    
    toast.success(`${deletedCount} outreach-uri invalide șterse`);
    fetchOutreach();
  };

  const handleBulkSend = async () => {
    // Filter only drafts with VALID emails
    const validDrafts = outreachList.filter(o => 
      o.status === 'draft' && isValidEmail(o.contact_email || o.target_email)
    );
    
    if (validDrafts.length === 0) {
      toast.error('Nu există drafturi cu email valid pentru trimitere');
      return;
    }
    
    const invalidCount = outreachList.filter(o => 
      o.status === 'draft' && !isValidEmail(o.contact_email || o.target_email)
    ).length;
    
    if (invalidCount > 0) {
      toast.info(`Se sar ${invalidCount} emailuri invalide`);
    }
    
    setSendingBulk(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const outreach of validDrafts) {
      try {
        await axios.post(
          `${API}/backlinks/outreach/${outreach.id}/send`,
          {},
          { headers: getAuthHeaders() }
        );
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to send to ${outreach.contact_email}:`, error);
      }
      // Small delay between emails
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    setSendingBulk(false);
    fetchOutreach();
    
    if (successCount > 0) {
      toast.success(`${successCount} emailuri trimise cu succes!`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} emailuri au eșuat`);
    }
  };

  const isRequested = (backlinkId) => {
    return requests.some(r => r.id === backlinkId || r.domain === backlinks.find(b => b.id === backlinkId)?.domain);
  };

  const hasOutreach = (backlinkId) => {
    return outreachList.some(o => o.backlink_id === backlinkId);
  };

  const filteredBacklinks = backlinks.filter(bl => 
    bl.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bl.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bl.type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getDaColor = (da) => {
    if (da >= 60) return 'text-green-500';
    if (da >= 40) return 'text-yellow-500';
    return 'text-orange-500';
  };

  const getTypeColor = (type) => {
    const colors = {
      'Guest Post': 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      'Directory': 'bg-green-500/10 text-green-500 border-green-500/20',
      'Forum': 'bg-purple-500/10 text-purple-500 border-purple-500/20',
      'Blog Comment': 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      'Resource Page': 'bg-pink-500/10 text-pink-500 border-pink-500/20',
      'Partnership': 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
    };
    return colors[type] || 'bg-gray-500/10 text-gray-500 border-gray-500/20';
  };

  const getStatusBadge = (status) => {
    const styles = {
      'draft': { class: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20', text: 'Draft' },
      'sent': { class: 'bg-blue-500/10 text-blue-500 border-blue-500/20', text: 'Trimis' },
      'responded': { class: 'bg-green-500/10 text-green-500 border-green-500/20', text: 'Răspuns' },
    };
    return styles[status] || styles['draft'];
  };

  if (!currentSite) {
    return (
      <div className="space-y-8" data-testid="backlinks-page">
        <div>
          <h1 className="font-heading text-3xl font-bold">Manager Backlinks</h1>
          <p className="text-muted-foreground mt-1">
            Generează oportunități de backlink bazate pe nișa site-ului
          </p>
        </div>
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <AlertCircle className="w-16 h-16 text-yellow-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Selectează un site</h3>
            <p className="text-muted-foreground text-center">
              Selectează un site din dropdown-ul din stânga sus pentru a vedea și genera backlinks relevante pentru nișa acelui site.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="backlinks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Manager Backlinks</h1>
          <p className="text-muted-foreground mt-1">
            Oportunități de backlink pentru <span className="text-primary font-medium">{currentSite.site_name || currentSite.site_url}</span>
          </p>
          {currentSite.niche && (
            <Badge variant="outline" className="mt-2">Nișă: {currentSite.niche}</Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleSearchNewOpportunities}
            disabled={searchingNew}
            variant="outline"
            className="border-primary/50"
            data-testid="search-new-button"
          >
            {searchingNew ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Se caută...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4 mr-2" />
                Caută Noi Oportunități
              </>
            )}
          </Button>
          <Button
            onClick={handleGenerateBacklinks}
            disabled={generating || !currentSite.niche}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="generate-backlinks-button"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Se generează...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generează Backlinks AI
              </>
            )}
          </Button>
        </div>
      </div>

      {!currentSite.niche && (
        <Card className="bg-yellow-500/10 border-yellow-500/20">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
              <p className="text-sm">
                Site-ul <strong>{currentSite.site_name}</strong> nu are o nișă definită. 
                Mergi la <strong>WordPress</strong>, șterge site-ul și adaugă-l din nou cu nișa specificată.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Link className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">{backlinks.length}</p>
                <p className="text-sm text-muted-foreground">Oportunități găsite</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <Mail className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">
                  {outreachList.filter(o => o.status === 'draft').length}
                </p>
                <p className="text-sm text-muted-foreground">Drafturi outreach</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Send className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">
                  {outreachList.filter(o => o.status === 'sent').length}
                </p>
                <p className="text-sm text-muted-foreground">Emailuri trimise</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">
                  {outreachList.filter(o => o.status === 'responded').length}
                </p>
                <p className="text-sm text-muted-foreground">Răspunsuri primite</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary">
          <TabsTrigger value="opportunities" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Search className="w-4 h-4 mr-2" />
            Oportunități ({backlinks.length})
          </TabsTrigger>
          <TabsTrigger value="outreach" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Mail className="w-4 h-4 mr-2" />
            Outreach ({outreachList.length})
          </TabsTrigger>
        </TabsList>

        {/* Opportunities Tab */}
        <TabsContent value="opportunities" className="space-y-6">
          {/* Search */}
          {backlinks.length > 0 && (
            <Card className="bg-card border-border">
              <CardContent className="pt-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Caută domeniu, categorie sau tip..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-secondary border-border"
                    data-testid="backlinks-search-input"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Backlinks List */}
          {backlinks.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Link className="w-16 h-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Niciun backlink generat</h3>
                <p className="text-muted-foreground text-center mb-6">
                  {currentSite.niche 
                    ? `Click pe "Generează Backlinks AI" pentru a găsi oportunități pentru nișa "${currentSite.niche}"`
                    : 'Adaugă o nișă site-ului pentru a genera backlinks relevante'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredBacklinks.map((backlink) => (
                <Card 
                  key={backlink.id} 
                  className="bg-card border-border hover:border-primary/30 transition-colors"
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <a 
                            href={`https://${backlink.domain}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-medium truncate hover:text-primary transition-colors"
                          >
                            {backlink.domain}
                          </a>
                          <ExternalLink className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        </div>
                        <div className="flex flex-wrap gap-1">
                          <Badge className={getTypeColor(backlink.type)}>
                            {backlink.type}
                          </Badge>
                          {backlink.price === 0 && (
                            <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                              Gratuit
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                      <div>
                        <p className={`text-lg font-bold font-mono ${getDaColor(backlink.da)}`}>
                          {backlink.da}
                        </p>
                        <p className="text-xs text-muted-foreground">DA</p>
                      </div>
                      <div>
                        <p className={`text-lg font-bold font-mono ${getDaColor(backlink.pa)}`}>
                          {backlink.pa}
                        </p>
                        <p className="text-xs text-muted-foreground">PA</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold font-mono text-primary">
                          {backlink.price === 0 ? 'Free' : `$${backlink.price}`}
                        </p>
                        <p className="text-xs text-muted-foreground">Preț</p>
                      </div>
                    </div>

                    {backlink.contact_info && (
                      <p className="text-xs text-muted-foreground mb-3 truncate">
                        📧 {backlink.contact_info}
                      </p>
                    )}

                    <Button
                      className={`w-full ${
                        hasOutreach(backlink.id) 
                          ? 'bg-green-500/20 text-green-500 border-green-500/30' 
                          : 'bg-primary text-primary-foreground hover:bg-primary/90'
                      }`}
                      disabled={preparingOutreach === backlink.id}
                      onClick={() => handlePrepareOutreach(backlink)}
                      data-testid={`outreach-${backlink.id}`}
                    >
                      {preparingOutreach === backlink.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Se pregătește...
                        </>
                      ) : hasOutreach(backlink.id) ? (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          Vezi Outreach
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Pregătește Outreach
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Outreach Tab */}
        <TabsContent value="outreach" className="space-y-4">
          {outreachList.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Inbox className="w-16 h-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Niciun outreach</h3>
                <p className="text-muted-foreground text-center">
                  Pregătește primul outreach din tab-ul "Oportunități"
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Filter buttons for drafts vs sent */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground mr-2">Filtrează:</span>
                <Button
                  variant={outreachFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOutreachFilter('all')}
                >
                  Toate ({outreachList.length})
                </Button>
                <Button
                  variant={outreachFilter === 'draft' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOutreachFilter('draft')}
                >
                  Drafturi ({outreachList.filter(o => o.status === 'draft').length})
                </Button>
                <Button
                  variant={outreachFilter === 'sent' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOutreachFilter('sent')}
                >
                  Trimise ({outreachList.filter(o => o.status === 'sent').length})
                </Button>
                <Button
                  variant={outreachFilter === 'responded' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOutreachFilter('responded')}
                >
                  Cu răspuns ({outreachList.filter(o => o.status === 'responded').length})
                </Button>
              </div>

              {/* Bulk Send Button - only for drafts with valid emails */}
              {outreachList.filter(o => o.status === 'draft').length > 0 && (
                <div className="flex justify-end gap-2 flex-wrap">
                  {/* Delete Invalid Button */}
                  {outreachList.filter(o => o.status === 'draft' && !isValidEmail(o.contact_email || o.target_email)).length > 0 && (
                    <Button
                      onClick={handleDeleteInvalidEmails}
                      variant="outline"
                      className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                      data-testid="delete-invalid-button"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Șterge Invalid ({outreachList.filter(o => o.status === 'draft' && !isValidEmail(o.contact_email || o.target_email)).length})
                    </Button>
                  )}
                  
                  {/* Bulk Send Button - only valid emails */}
                  {outreachList.filter(o => o.status === 'draft' && isValidEmail(o.contact_email || o.target_email)).length > 0 && (
                    <Button
                      onClick={handleBulkSend}
                      disabled={sendingBulk}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="bulk-send-button"
                    >
                      {sendingBulk ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Se trimit...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Aprobă & Trimite ({outreachList.filter(o => o.status === 'draft' && isValidEmail(o.contact_email || o.target_email)).length} valide)
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )}
              
              {/* Bulk Delete Button - for sent emails */}
              {outreachFilter === 'sent' && outreachList.filter(o => o.status === 'sent').length > 0 && (
                <div className="flex justify-end">
                  <Button
                    onClick={() => handleBulkDelete('sent')}
                    variant="destructive"
                    size="sm"
                    data-testid="bulk-delete-sent-button"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Șterge toate trimise ({outreachList.filter(o => o.status === 'sent').length})
                  </Button>
                </div>
              )}
              
              <div className="space-y-3">
              {outreachList
                .filter(o => outreachFilter === 'all' || o.status === outreachFilter)
                .map((outreach) => {
                const statusBadge = getStatusBadge(outreach.status);
                const emailValid = isValidEmail(outreach.contact_email || outreach.target_email);
                return (
                  <Card key={outreach.id} className={`bg-card border-border ${!emailValid && outreach.status === 'draft' ? 'border-red-500/30 bg-red-500/5' : ''}`}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{outreach.backlink_domain || outreach.target_site}</span>
                            <Badge className={statusBadge.class}>{statusBadge.text}</Badge>
                            {!emailValid && outreach.status === 'draft' && (
                              <Badge className="bg-red-500/10 text-red-500 border-red-500/20">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Email Invalid
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">
                            📄 {outreach.article_title}
                          </p>
                          <p className={`text-xs mt-1 ${!emailValid ? 'text-red-400' : 'text-muted-foreground'}`}>
                            📧 {outreach.contact_email || outreach.target_email || 'Fără email'}
                            {!emailValid && ' ⚠️'}
                          </p>
                          {outreach.sent_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              🕐 Trimis: {new Date(outreach.sent_at).toLocaleString('ro-RO')}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {outreach.status === 'draft' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setCurrentOutreach(outreach);
                                  setEditSubject(outreach.email_subject);
                                  setEditBody(outreach.email_body);
                                  setShowOutreachDialog(true);
                                  setEditingEmail(false);
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleSendEmail(outreach.id)}
                                disabled={sendingEmail === outreach.id || !outreach.contact_email}
                                className="bg-primary text-primary-foreground"
                              >
                                {sendingEmail === outreach.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Send className="w-4 h-4" />
                                )}
                              </Button>
                            </>
                          )}
                          
                          {outreach.status === 'sent' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleMarkResponded(outreach.id, 'positive')}
                                className="text-green-500 border-green-500/30"
                                title="Marcare răspuns pozitiv"
                              >
                                <CheckCheck className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleMarkResponded(outreach.id, 'negative')}
                                className="text-red-500 border-red-500/30"
                                title="Marcare răspuns negativ"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (window.confirm(`Sigur vrei să ștergi outreach-ul pentru ${outreach.backlink_domain || outreach.target_site}?`)) {
                                handleDeleteOutreach(outreach.id);
                              }
                            }}
                            className="text-destructive hover:text-destructive"
                            title="Șterge"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Outreach Preview/Edit Dialog */}
      <Dialog open={showOutreachDialog} onOpenChange={setShowOutreachDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Outreach Email
            </DialogTitle>
            <DialogDescription>
              {currentOutreach?.backlink_domain} - {editingEmail ? 'Editare' : 'Preview'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Article Info */}
            <div className="p-3 rounded-lg bg-secondary/50">
              <p className="text-xs text-muted-foreground mb-1">Articol selectat:</p>
              <p className="text-sm font-medium">{currentOutreach?.article?.title || currentOutreach?.article_title}</p>
              {(currentOutreach?.article?.wordpress_url || currentOutreach?.article_url) && (
                <a 
                  href={currentOutreach?.article?.wordpress_url || currentOutreach?.article_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline"
                >
                  {currentOutreach?.article?.wordpress_url || currentOutreach?.article_url}
                </a>
              )}
            </div>

            {/* Email Preview/Edit */}
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium">Subiect</Label>
                {editingEmail ? (
                  <Input
                    value={editSubject}
                    onChange={(e) => setEditSubject(e.target.value)}
                    className="mt-1 bg-secondary"
                  />
                ) : (
                  <p className="mt-1 p-2 rounded bg-secondary text-sm">{editSubject}</p>
                )}
              </div>
              
              <div>
                <Label className="text-sm font-medium">Corp email</Label>
                {editingEmail ? (
                  <Textarea
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    className="mt-1 bg-secondary min-h-[200px]"
                  />
                ) : (
                  <div className="mt-1 p-3 rounded bg-secondary text-sm whitespace-pre-wrap">
                    {editBody}
                  </div>
                )}
              </div>

              <div className="p-2 rounded bg-muted/50">
                <p className="text-xs text-muted-foreground">
                  📧 Se va trimite la: <strong>{currentOutreach?.contact_email || 'Email nedisponibil'}</strong>
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 gap-2">
            <Button
              variant="outline"
              onClick={() => setEditingEmail(!editingEmail)}
            >
              <Edit3 className="w-4 h-4 mr-2" />
              {editingEmail ? 'Anulează Editarea' : 'Editează'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowOutreachDialog(false)}
            >
              Închide
            </Button>
            <Button
              onClick={() => handleSendEmail()}
              disabled={sendingEmail || !currentOutreach?.contact_email}
              className="bg-primary text-primary-foreground"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Se trimite...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Trimite Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
